import styled, { css } from "styled-components";
import {
  getImageLoader,
  HeroTwoCols,
  Image,
  LinkCard,
  ImageCopyItem,
  InfoCTA,
  LinkNext as DLSLink,
  PageSection,
  PressLogos,
  LinkType,
} from "@literati/public-ui-shared-react";
import AnimateOnScroll from "components/AnimateOnScroll";
import TestimonialCarousel from "components/TestimonialCarousel/TestimonialCarousel";
import landingConfig, {
  LANDING_CTA_LINK,
  LandingConfigType,
} from "components/Landing/LandingConfig";
import LandingIgniteSection from "components/Landing/LandingIgniteSection";
import LandingLiteracyStatsSection from "components/Landing/LandingLiteracyStatsSection";
import Head from "next/head";
import OpenGraph from "components/Layout/OpenGraph";
import { widthFromHeight } from "utils/images";
import { useRouter } from "next/router";

const Container = styled.section`
  color: ${({ theme }) => theme.colors["ink-black"]};
`;

const itemsGrid = css`
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-template-columns: 1fr;
  gap: 2.625rem 0;
  justify-items: center;

  ${({ theme }) => theme.media.lg} {
    gap: 0 2rem;
  }

  ${({ theme }) => theme.media.lg} {
    grid-template-rows: 1fr;
    grid-template-columns: repeat(3, 1fr);
    gap: 0 3rem;
  }
`;

const breakNone = css`
  br {
    display: none;
  }
`;
const breakBlock = css`
  br {
    display: block;
  }
`;

interface FullWidthCroppedImageProps {
  $url: string;
  $height: number;
  $width: number;
}

const fullWidthCroppedImage = css<FullWidthCroppedImageProps>`
  background-image: ${({ $url, $height, $width }) =>
    `url(${getImageLoader({
      src: $url,
      width: widthFromHeight($height / 2, $width / 2, $height / 2),
    })})`};
  height: ${({ $height }) => `${$height / 2}px`};
  background-position: center top;
  background-repeat: no-repeat;
  width: 100vw;

  ${({ theme }) => theme.media.sm} {
    background-image: ${({ $url, $height, $width }) =>
      `url(${getImageLoader({
        src: $url,
        width: widthFromHeight($height, $width, $height),
      })})`};
    height: ${({ $height }) => `${$height}px`};
  }
`;

const Hero = styled(HeroTwoCols)<{ $targetHeight: number }>`
  ${breakNone}

  ${({ theme }) => theme.media.xs} {
    ${breakBlock}
  }

  .hero-two-cols-copy {
    padding: 1rem 1rem 0;
  }

  .hero-two-cols-image-layout {
    padding: 0 1rem;
    max-width: ${({ $targetHeight }) => `${$targetHeight}px`};
  }

  ${({ theme }) => theme.media.lg} {
    .hero-two-cols-image-layout {
      padding: 0;
      max-width: none;
    }

    .hero-two-cols-copy {
      padding: 0;
    }
  }

  ${({ theme }) => theme.media.xl} {
    .hero-two-cols-image-layout {
      flex: 0 0 55%;
    }

    .hero-two-cols-copy {
      flex: 0 0 45%;
      padding: 1rem 1rem 0;
    }
  }

  /* Literacy-focused hero (ex–lit-5 funnel winner) */
  .hero-copy-heading {
    color: ${({ theme }) => theme.colors["ink-black"]};
    font-size: 3.375rem;
    line-height: 3.75rem;
    font-weight: ${({ theme }) => theme.font.weight.roman};
    font-family: ${({ theme }) => theme.font.fontFamily.hoeflerTitling};

    em {
      color: ${({ theme }) => theme.colors["ink-black"]};
      font-size: 3.375rem;
      font-style: italic;
      font-weight: ${({ theme }) => theme.font.weight.roman};
      font-family: ${({ theme }) => theme.font.fontFamily.hoeflerTitling};
      line-height: 3.75rem;
    }
  }

  p.hero-copy-subtext {
    font-family: ${({ theme }) => theme.font.fontFamily.avenir};
    color: ${({ theme }) => theme.colors["ink-black"]};
    font-size: 1.25rem;
    line-height: 1.875rem;
    margin-top: 1.5rem;
  }

  .hero-two-cols-copy a {
    color: ${({ theme }) => theme.colors["ink-black"]};
    text-align: center;
    font-size: 1rem;
    font-weight: ${({ theme }) => theme.font.weight.black};
    line-height: 1.5rem;
    text-transform: capitalize;
    font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  }

  .hero-two-cols-copy p:not(.hero-copy-subtext) {
    color: #72716f;
    font-family: ${({ theme }) => theme.font.fontFamily.avenir};
    font-size: 0.875rem;
    line-height: 1.3125rem;
  }

  @media (max-width: 900px) {
    .hero-copy-heading,
    .hero-copy-heading em {
      font-size: 2.25rem;
    }

    p.hero-copy-subtext br {
      display: none;
    }
  }

  @media (max-width: 560px) {
    .hero-copy-heading br {
      display: none;
    }

    .hero-copy-heading {
      max-width: 200px;
      line-height: 2.7rem !important;
    }

    .hero-copy-heading em {
      line-height: 2.7rem !important;
    }

    p.hero-copy-subtext {
      font-size: 1.125rem !important;
      line-height: 1.6875rem !important;
      margin-top: 1rem !important;
    }
  }
`;

const PreferencesClaimImage = styled.div<FullWidthCroppedImageProps>`
  ${fullWidthCroppedImage}

  margin: 2.25rem 0 0;

  ${({ theme }) => theme.media.lg} {
    margin: 3.5rem 0 0;
  }
`;
const PreferencesClaimLinkContainer = styled.div`
  padding: 0 1.25rem;

  ${({ theme }) => theme.media.sm} {
    padding: 0 3rem;
  }
`;
const PreferencesClaimLink = styled(DLSLink)`
  margin: 2rem auto 0;
`;

const HowItWorksContent = styled.div`
  margin: 3rem 0;

  ${({ theme }) => theme.media.sm} {
    margin: 3rem 1rem;
  }

  ${({ theme }) => theme.media.lg} {
    margin: 3rem;
  }
`;
const HowItWorksHeading = styled.h2`
  font-family: hoefler-titling, serif;
  font-size: 2rem;
  margin: 1rem 0 0;
  ${breakNone}

  ${({ theme }) => theme.media.xs} {
    ${breakBlock}
  }

  ${({ theme }) => theme.media.lg} {
    font-size: 2.25rem;
    ${breakNone}
  }
`;
const HowItWorksItemsGrid = styled.div`
  ${itemsGrid};
  margin: 3rem 0;
`;
const HowItWorksImageCopyItem = styled(ImageCopyItem)`
  .image-copy-item-primary-copy {
    margin: 0;
  }

  ${({ theme }) => theme.media.lg} {
    ${breakNone}

    .image-copy-item-primary-copy {
      margin: 1rem 0 0;
    }
  }

  ${({ theme }) => theme.media.xl} {
    ${breakBlock}
  }
`;

const IgniteInsetPageSection = styled(PageSection)`
  &.page-section {
    background-image: linear-gradient(
      to bottom,
      rgb(246, 246, 245) 50%,
      ${({ theme }) => theme.colors["french-blue-tint-4"]} 0%
    ) !important;
  }

  .container-inset-card-inset-border {
    margin: 0 !important;
    border-radius: 12px;
    box-sizing: border-box;
  }
`;

const PersonalizedSkillsPageSection = styled(PageSection)`
  .page-section-header-content-spacing {
    padding: 0;
  }
  .page-section-subtext {
    padding: 0 1.25rem;
  }

  ${({ theme }) => theme.media.sm} {
    .page-section-header-content-spacing,
    .page-section-subtext {
      padding: 0 3rem;
    }

    ${breakNone}
  }
`;
const PersonalizedSkillItem = styled.div<{ $evenIdx: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 4rem 0 0;

  > div {
    flex: 0 0 50%;
  }

  ${({ theme }) => theme.media.lg} {
    flex-direction: ${({ $evenIdx }) => ($evenIdx ? "row-reverse" : "row")};
    margin: 6rem 0 0;
  }
`;
const PersonalizedSkillsImageContainer = styled.div<{ $evenIdx: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;

  ${({ theme }) => theme.media.lg} {
    justify-content: ${({ $evenIdx }) =>
      $evenIdx ? "flex-end" : "flex-start"};
    max-height: 500px;
  }
`;
const PersonalizedSkillsInfoCTAContainer = styled.div<{ $evenIdx: boolean }>`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.media.lg} {
    justify-content: ${({ $evenIdx }) =>
      $evenIdx ? "flex-start" : "flex-end"};
  }
`;
const PersonalizedSkillsInfoCTA = styled(InfoCTA)`
  margin: 1.25rem 0 0;

  ${({ theme }) => theme.media.lg} {
    margin: 0;
    max-width: 460px;
  }
`;
const PersonalizedSkillsDescPrimary = styled.p`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 1rem;

  ${({ theme }) => theme.media.lg} {
    font-size: 1.25rem;
  }
`;
// Container temporary for 1.1 copy update test
// @TODO: update link styles so that `a` inherits children styles
const PersonalizedSkillsLinkContainer = styled.div`
  ${({ theme }) => theme.media.md} {
    > a {
      max-width: 300px;
    }
  }
`;
const PersonalizedSkillsLink = styled(DLSLink)`
  margin: 1.5rem auto 0;

  ${({ theme }) => theme.media.md} {
    max-width: 300px;
  }

  ${({ theme }) => theme.media.lg} {
    margin: 1.5rem 0 0;
  }
`;

const TestimonialsPageSection = styled(PageSection)`
  ${({ theme }) => theme.media.sm} {
    ${breakNone}
  }
`;
const TCarousel = styled(TestimonialCarousel)`
  padding: 4rem 2rem 0;

  ${({ theme }) => theme.media.lg} {
    padding: 6rem 0 0;
  }
`;
const TestimonialLinkContainer = styled.div`
  padding: 0 1.25rem;

  ${({ theme }) => theme.media.sm} {
    padding: 0 3rem;
  }
`;
const TestimonialLink = styled(DLSLink)`
  margin: 2rem auto 0;
`;

const ExploreContent = styled.div`
  ${itemsGrid}
  grid-template-rows: repeat(2, 1fr);
  margin: 2rem 0 0;

  ${({ theme }) => theme.media.lg} {
    grid-template-rows: 1fr;
    grid-template-columns: repeat(2, 1fr);
    margin: 3.5rem 0 0;

    > .grid-item-link-card:first-of-type {
      justify-self: end;
    }

    > .grid-item-link-card:last-of-type {
      justify-self: start;
    }
  }
`;

const StyledLinkCard = styled(LinkCard)<{ $width: number }>`
  max-width: ${({ $width }) => `${$width / 2}px`};
`;

const FooterImageContainer = styled.div`
  background: ${({ theme }) => theme.colors["papier-blanc"]};
  margin: 0 0 -1.5rem;
`;

const FooterImage = styled.div<FullWidthCroppedImageProps>`
  ${fullWidthCroppedImage}
`;

export type LandingProps = {
  className?: string;
  config?: LandingConfigType;
  testimonials: any;
  headData?: any;
};

/**
 * Redesign of literati.com home page with a focus on kids SEO.
 */
function Landing({
  className,
  config: config = landingConfig,
  testimonials,
  headData,
}: LandingProps) {
  const router = useRouter();

  const ctaLink = `${LANDING_CTA_LINK}${router.asPath}`;

  return (
    <Container className={className}>
      {headData && (
        <>
          <Head>
            <title key="title">{headData?.fields?.title}</title>
          </Head>
          <OpenGraph
            openGraph={{
              og_title: headData?.fields?.title,
              meta_description: headData?.fields?.description,
              og_description: headData?.fields?.description,
              images: [
                getImageLoader({
                  src:
                    headData?.fields?.openGraphImage?.fields?.file?.url || "",
                  width:
                    headData?.fields?.openGraphImage?.fields?.file?.details
                      ?.image?.width || 1200,
                }),
              ],
            }}
          />
        </>
      )}
      <Hero
        {...config.hero}
        linkProps={{
          href: `/kids${ctaLink}`,
        }}
        $targetHeight={config.hero.imageProps.targetHeight}
      />

      <LandingLiteracyStatsSection />

      <AnimateOnScroll animation="animation-crossfade" threshold={0.1}>
        <div>
          <IgniteInsetPageSection
            gradient={config.subscriberStats.gradient}
            insetCard
            removeSectionPadding
          >
            <LandingIgniteSection />
          </IgniteInsetPageSection>
        </div>
      </AnimateOnScroll>
      <PageSection
        bgColor={config.preferencesClaim.bgColor}
        primaryHeading={config.preferencesClaim.primaryHeading}
        removeContentSpacing
        subtext={config.preferencesClaim.subtext}
      >
        <AnimateOnScroll animation="animation-fade-up-in" threshold={0.1}>
          <PreferencesClaimImage
            $height={config.preferencesClaim.imageProps.height}
            $width={config.preferencesClaim.imageProps.width}
            $url={config.preferencesClaim.imageProps.src}
          />
        </AnimateOnScroll>
        <PreferencesClaimLinkContainer>
          <PreferencesClaimLink
            href={ctaLink}
            variant="button"
            /* @todo - This prop should be optional, but TypeScript thinks it's required here.
             * Look more closely at LinkNext prop type later */
            legacyBehavior={undefined}
          >
            Start Your Book Box
          </PreferencesClaimLink>
        </PreferencesClaimLinkContainer>
      </PageSection>
      <PageSection
        gradient={config.howItWorks.gradient}
        insetCard
        removeSectionPadding
      >
        <HowItWorksContent>
          <h2 className="eyebrow">{config.howItWorks.eyebrow}</h2>
          <HowItWorksHeading>{config.howItWorks.heading}</HowItWorksHeading>
          <HowItWorksItemsGrid>
            {config.howItWorks.items.map((content, idx) => (
              <HowItWorksImageCopyItem
                key={`reading-stage-item-${idx}`}
                variant="small"
                {...content}
              />
            ))}
          </HowItWorksItemsGrid>
        </HowItWorksContent>
      </PageSection>
      <PersonalizedSkillsPageSection
        bgColor={config.personalizedSkills.bgColor}
        primaryHeading={config.personalizedSkills.primaryHeading}
        subtext={config.personalizedSkills.subtext}
      >
        {config.personalizedSkills.items.map((item, idx) => {
          const evenIdx = idx % 2 === 0;
          const skillAnimation = evenIdx
            ? "animation-fade-left-in"
            : "animation-fade-right-in";
          return (
            <PersonalizedSkillItem
              $evenIdx={evenIdx}
              key={`personalized-skill-${idx}`}
            >
              <AnimateOnScroll animation={skillAnimation} threshold={0.1}>
                <PersonalizedSkillsImageContainer $evenIdx={evenIdx}>
                  <Image {...item.imageProps} />
                </PersonalizedSkillsImageContainer>
              </AnimateOnScroll>
              <PersonalizedSkillsInfoCTAContainer $evenIdx={evenIdx}>
                <PersonalizedSkillsInfoCTA
                  borderColor={item.infoCTAProps.borderColor}
                  primaryHeading={item.infoCTAProps.primaryHeading}
                  ribbonProps={{ ...item.infoCTAProps.ribbonProps }}
                >
                  <PersonalizedSkillsDescPrimary>
                    {item.infoCTAProps.description.primary}
                  </PersonalizedSkillsDescPrimary>
                  <p>{item.infoCTAProps.description.subtext}</p>
                  <PersonalizedSkillsLinkContainer>
                    <PersonalizedSkillsLink
                      href={ctaLink}
                      variant="button"
                      legacyBehavior={undefined}
                    >
                      {item.ctaLabel}
                    </PersonalizedSkillsLink>
                  </PersonalizedSkillsLinkContainer>
                </PersonalizedSkillsInfoCTA>
              </PersonalizedSkillsInfoCTAContainer>
            </PersonalizedSkillItem>
          );
        })}
      </PersonalizedSkillsPageSection>
      <PageSection gradient={config.pressLogos.gradient} removeSectionPadding>
        <PressLogos className="w-full" />
      </PageSection>
      <TestimonialsPageSection
        bgColor={config.testimonials.bgColor}
        primaryHeading={config.testimonials.primaryHeading}
        removeContentSpacing
      >
        <TCarousel items={testimonials} />
        <TestimonialLinkContainer>
          <TestimonialLink
            href={ctaLink}
            variant="button"
            legacyBehavior={undefined}
          >
            Get Started
          </TestimonialLink>
        </TestimonialLinkContainer>
      </TestimonialsPageSection>
      <PageSection
        bgColor={config.explore.bgColor}
        eyebrow={config.explore.eyebrow}
        primaryHeading={config.explore.primaryHeading}
      >
        <ExploreContent>
          {config.explore.items.map((item, idx) => {
            const { location, ...buttonProps } = item;
            const isSubscribeLink = location === LANDING_CTA_LINK;
            const { LINK, LINK_NEXT } = LinkType;
            return (
              <AnimateOnScroll
                animation="animation-fade-up-in"
                key={`explore-item-${idx}`}
                threshold={0.1}
              >
                <div className="grid-item-link-card">
                  <StyledLinkCard
                    {...buttonProps}
                    linkType={isSubscribeLink ? LINK_NEXT : LINK}
                    href={location}
                    $width={item.imageProps.width}
                  />
                </div>
              </AnimateOnScroll>
            );
          })}
        </ExploreContent>
      </PageSection>
      <FooterImageContainer>
        <FooterImage
          $height={config.footerImage.height}
          $width={config.footerImage.width}
          $url={config.footerImage.src}
        />
      </FooterImageContainer>
    </Container>
  );
}

export default Landing;
